import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Drawer } from 'antd';

import { StoreI } from '~src/rootReducer';
import { ButtonShape, ButtonSizes } from '~src/shared/buttons/basicButtons/BaseButton.sc';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { deleteSubobjective, getSubobjectives, postSubobjective } from '~src/store/okrs/actions';
import { ObjectiveItem, SubObjectiveItem } from '~src/store/okrs/interfaces';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import { CapitalizeFirst, LoadingWrapper, SubObjectivesList, Title } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { ApiReduxResp } from '~src/services/apiMiddleware';

import AddSubobjectiveForm from './AddSubobjectiveForm';
import EditSubobjectiveItem from './EditSubObjectiveItem';
import EditSubobjectiveDetails from './EditSubobjectiveDetails';

interface DispatchProps {
  fetchSubobjectives(objectiveId: string): void;

  attachSubobjective(objectiveId: string, subobjectiveId: string, weighting: number | null): ApiReduxResp;

  detachSubobjective(objectiveId: string, subobjectiveId: string): ApiReduxResp;
}

interface OwnProps {
  superObjective: ObjectiveItem;
  readonly: boolean;
}

interface StateProps {
  subobjectiveState?: { isLoading: boolean; objectives?: SubObjectiveItem[]; error?: object };
}

interface Props extends DispatchProps, OwnProps, StateProps {}

const EditSubObjectiveList: FC<Props> = props => {
  const [t] = useTranslation();

  const [selectedSubobjective, setSelectedSubobjective] = useState<SubObjectiveItem | null>(null);
  const resetSelectedSubobjective = () => {
    setSelectedSubobjective(null);
  };
  const [addForm, setAddForm] = useState(false);
  const showAddForm = () => setAddForm(true);
  const hideAddForm = () => setAddForm(false);

  useEffect(() => {
    if (!props.subobjectiveState) {
      props.fetchSubobjectives(props.superObjective.id);
    }
  }, [props.subobjectiveState]);

  const existingSubobjectives: SubObjectiveItem[] =
    (props.subobjectiveState && props.subobjectiveState.objectives) || [];

  const detachSubobjective = (subobjectiveId: string) =>
    props.detachSubobjective(props.superObjective.id, subobjectiveId);

  const attachSubobjective = (subobjectiveId: string, weighting: number | null) =>
    props.attachSubobjective(props.superObjective.id, subobjectiveId, weighting);

  return (
    <>
      <Dialog show={addForm} onHide={hideAddForm}>
        <div>
          {addForm && (
            <AddSubobjectiveForm
              addSubobjective={attachSubobjective}
              disallowedIds={existingSubobjectives.map(o => o.id).concat(props.superObjective.id)}
              hide={hideAddForm}
            />
          )}
        </div>
      </Dialog>
      <Drawer
        width={(window.innerWidth - 1170) / 2 + 1170 * 0.33}
        onClose={resetSelectedSubobjective}
        open={!!selectedSubobjective}
      >
        {selectedSubobjective && (
          <EditSubobjectiveDetails
            subobjective={selectedSubobjective}
            subobjectives={existingSubobjectives}
            detachSubobjective={detachSubobjective}
            attachSubobjective={attachSubobjective}
            close={resetSelectedSubobjective}
          />
        )}
      </Drawer>
      <SelectorHandler iconName="iconPlusBox" hoverTextKey="x.objective.sub-objectives.title.text">
        {() => (
          <>
            <Title $active className="sub_title">
              <span data-translation={'x.objective.sub-objectives.title.text'}>
                {t('x.objective.sub-objectives.title.text')}
              </span>
              <HoverInfoIcon
                infoText={{
                  key: 'x.objective.sub-objectives.title.help',
                  txt: t('x.objective.sub-objectives.title.help'),
                }}
              />
            </Title>
            {props.subobjectiveState && props.subobjectiveState.isLoading && (
              <LoadingWrapper>
                <LoadingSpinner alignCls="center" />
              </LoadingWrapper>
            )}
            <SubObjectivesList>
              {existingSubobjectives.map(objective => {
                return (
                  <EditSubobjectiveItem
                    key={objective.id}
                    objective={objective}
                    readonly={props.readonly}
                    unlinkSubobjective={detachSubobjective}
                    setSelectedSubobjective={setSelectedSubobjective}
                  />
                );
              })}
            </SubObjectivesList>
            <SecondaryButton
              onClick={showAddForm}
              $buttonSize={ButtonSizes.Medium}
              $buttonShape={ButtonShape.Oval}
              style={{ margin: '10px 0' }}
            >
              <CapitalizeFirst data-translation="x.objective.sub-objectives.add.text">
                {t('x.objective.sub-objectives.add.text')}
              </CapitalizeFirst>
            </SecondaryButton>
          </>
        )}
      </SelectorHandler>
    </>
  );
};

const mapStateToProps = (state: StoreI, ownProps: OwnProps): StateProps => ({
  subobjectiveState: state.okrs.objectives.subobjectives[ownProps.superObjective.id],
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchSubobjectives: (objectiveId: string) => dispatch(getSubobjectives(objectiveId)),
  attachSubobjective: (superObjectiveId: string, subobjectiveId: string, weighting: number | null) =>
    dispatch(postSubobjective(superObjectiveId, subobjectiveId, weighting)),
  detachSubobjective: (objectiveId: string, subobjectiveId: string) =>
    dispatch(deleteSubobjective(objectiveId, subobjectiveId)),
});

const EditSubObjectiveListC = connect(mapStateToProps, mapDispatchToProps)(EditSubObjectiveList);

export default EditSubObjectiveListC;
